import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Collapse, type SxProps, type Theme, useTheme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { ALERT_COLORS, CHIP_COLORS, EMBER_ORANGE } from "@src/appV2/lib/Theme/constants";
import { useState } from "react";

import { Button, type ButtonVariant } from "../Button";
import { Icon, type IconType } from "../Icon";
import { Text } from "../Text";

export const INFO_BANNER_VARIANTS = ["info", "warning", "error", "success"] as const;
export type InfoBannerVariant = (typeof INFO_BANNER_VARIANTS)[number];

interface InfoBannerAction {
  label: string;
  onClick: () => void;
  bold?: boolean;
  variant?: ButtonVariant;
}

export interface InfoBannerProps {
  variant: InfoBannerVariant;
  title?: React.ReactNode;
  message: React.ReactNode;
  icon?: React.ReactNode;
  action?: InfoBannerAction;
  actionPlacement?: "end" | "bottom";
  sx?: SxProps<Theme>;
  collapsible?: boolean;
}

const DEFAULT_ICONS: Record<InfoBannerVariant, IconType> = {
  info: "information",
  warning: "warning",
  error: "warning",
  success: "badge-check",
};

const BORDER_COLORS: Record<InfoBannerVariant, string> = {
  info: "rgba(24,144,255,0.2)",
  warning: "#FFE0B2",
  error: "#FFCDD2",
  success: "#A5D6A7",
};

const ICON_COLORS: Record<InfoBannerVariant, string> = {
  info: "#1890FF",
  warning: EMBER_ORANGE,
  error: "#DD1717",
  success: CHIP_COLORS.success.color,
};

const BACKGROUND_COLORS: Record<InfoBannerVariant, string> = {
  info: ALERT_COLORS.info.backgroundColor,
  warning: ALERT_COLORS.warning.backgroundColor,
  error: ALERT_COLORS.error.backgroundColor,
  success: ALERT_COLORS.success.backgroundColor,
};

interface VariantColors {
  background: string;
  border: string;
  iconColor: string;
}

function getVariantColors(variant: InfoBannerVariant, theme: Theme): VariantColors {
  if (variant === "info") {
    return {
      background: theme.palette.background?.baseDarker ?? BACKGROUND_COLORS[variant],
      border: theme.palette.border?.muted ?? BORDER_COLORS[variant],
      iconColor: theme.palette.base?.strong ?? ICON_COLORS[variant],
    };
  }

  return {
    background: BACKGROUND_COLORS[variant],
    border: BORDER_COLORS[variant],
    iconColor: ICON_COLORS[variant],
  };
}

export function InfoBanner(props: InfoBannerProps) {
  const { variant, title, message, icon, action, actionPlacement = "end", sx, collapsible } = props;

  const isCollapsible = Boolean(collapsible);
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();

  const handleToggle = () => {
    if (isCollapsible) {
      setIsExpanded((previous) => !previous);
    }
  };

  const defaultIcon = DEFAULT_ICONS[variant];
  const colors = getVariantColors(variant, theme);

  const actionFontWeight = action?.bold ? theme.typography.fontWeightBold : undefined;
  const actionVariant = action?.variant ?? "link";

  const messageNode = (
    <Text
      sx={(theme: Theme) => ({
        color: theme.palette.base?.base,
      })}
      variant="body2"
    >
      {message}
    </Text>
  );

  return (
    <Box
      sx={mergeSxProps(
        (theme: Theme) => ({
          display: "flex",
          alignItems: "flex-start",
          gap: theme.spacing(3),
          padding: theme.spacing(4),
          backgroundColor: colors.background,
          border: `${theme.borderWidth?.regular} solid ${colors.border}`,
          borderRadius: theme.borderRadius?.large,
          width: "100%",
        }),
        sx
      )}
    >
      <Box
        sx={(theme: Theme) => ({
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: colors.iconColor,
          // Match the line box of the first line of body2 text so the icon
          // visually centers against it. With multi-line text the outer
          // container's `alignItems: flex-start` keeps this box at the top,
          // so the icon stays aligned with the first line only.
          minHeight: `calc(${theme.typography.body2.fontSize} * ${
            theme.typography.body2.lineHeight ?? 1.5
          })`,
        })}
      >
        {icon ?? <Icon size="large" type={defaultIcon} />}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          component={isCollapsible ? "button" : "div"}
          type={isCollapsible ? "button" : undefined}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            background: "none",
            border: "none",
            padding: 0,
            cursor: isCollapsible ? "pointer" : "default",
            textAlign: "left",
          }}
          onClick={handleToggle}
        >
          <Box sx={{ flex: 1 }}>
            {title && (
              <Text
                semibold
                sx={(theme: Theme) => ({
                  color: theme.palette.base?.base,
                  marginBottom: isCollapsible && !isExpanded ? 0 : theme.spacing(1),
                })}
                variant="body2"
              >
                {title}
              </Text>
            )}
            {!isCollapsible && messageNode}
          </Box>
          {isCollapsible && (
            <ExpandMoreIcon
              fontSize="small"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease-in-out",
                marginLeft: theme.spacing(2),
              })}
            />
          )}
        </Box>
        {isCollapsible && <Collapse in={isExpanded}>{messageNode}</Collapse>}
        {actionPlacement === "bottom" && action && (
          <Box sx={(theme: Theme) => ({ marginTop: theme.spacing(3) })}>
            <Button
              size="small"
              variant={actionVariant}
              onClick={action.onClick}
              sx={{
                paddingLeft: actionVariant === "link" ? 0 : undefined,
                fontWeight: actionFontWeight,
              }}
            >
              {action.label}
            </Button>
          </Box>
        )}
      </Box>

      {actionPlacement === "end" && action && (
        <Box
          sx={{
            flexShrink: 0,
            // Make the wrapper a flex container so its height collapses to
            // the Button's 24px — without this, the default block Box creates
            // a line box that inherits the parent's line-height and ends up
            // taller than the Button itself.
            display: "flex",
            // Nudge the button up so its content center aligns with the first
            // line of body2 text. The small button is 24px tall while the
            // body2 first-line box is 21px (14px * 1.5), so their centers
            // differ by (24 - 21) / 2 = 1.5px.
            marginTop: "-1.5px",
          }}
        >
          <Button
            size="small"
            variant={actionVariant}
            onClick={action.onClick}
            sx={{ fontWeight: actionFontWeight }}
          >
            {action.label}
          </Button>
        </Box>
      )}
    </Box>
  );
}
